import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Autocomplete,
  Chip,
  Grid,
  CircularProgress,
  Collapse,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Skeleton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  FileDownload as DownloadIcon,
  TableChartOutlined as TableIcon,
  Check as CheckIcon,
  VisibilityOutlined as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import axios from 'axios';
import { MarkingCodeFilters, FilterStatusOption, FilterTypeOption } from '../types';
import { API_BASE, APP_VERSION } from '../constants';

interface MarkingCodesExportDialogProps {
  open: boolean;
  onClose: () => void;
  initialFilters: MarkingCodeFilters;
  pharmacies: string[];
  storages: string[];
  statuses: FilterStatusOption[];
  types: FilterTypeOption[];
}

const AVAILABLE_COLUMNS = [
  { key: 'code', label: 'Код маркировки' },
  { key: 'product', label: 'Товар' },
  { key: 'type', label: 'Тип документа' },
  { key: 'status', label: 'Статус накладной' },
  { key: 'documentNumber', label: '№ документа' },
  { key: 'sender', label: 'Отправитель' },
  { key: 'receiver', label: 'Получатель' },
  { key: 'date', label: 'Дата записи' }
];

export const MarkingCodesExportDialog: React.FC<MarkingCodesExportDialogProps> = ({
  open,
  onClose,
  initialFilters,
  pharmacies,
  storages,
  statuses,
  types
}) => {
  const theme = useTheme();

  // ── Состояния ──
  const [exportFormat, setExportFormat] = useState<'xlsx' | 'csv'>('xlsx');
  const [periodPreset, setPeriodPreset] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [materialOptions, setMaterialOptions] = useState<string[]>([]);
  const [materialsLoading, setMaterialsLoading] = useState<boolean>(false);

  const [selectedPharmacies, setSelectedPharmacies] = useState<string[]>([]);
  const [selectedStorages, setSelectedStorages] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<(string | number)[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<(string | number)[]>([]);

  const [showColumnsConfig, setShowColumnsConfig] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    AVAILABLE_COLUMNS.map((c) => c.key)
  );

  // Предварительный подсчет и строки для предпросмотра
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewRows, setPreviewRows] = useState<any[]>([]);
  const [isCounting, setIsCounting] = useState<boolean>(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [showPreviewTable, setShowPreviewTable] = useState<boolean>(true);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const abortRef = useRef<AbortController | null>(null);

  // Загрузка списка товаров
  const fetchMaterials = useCallback(async (query: string) => {
    try {
      setMaterialsLoading(true);
      const res = await axios.get(`${API_BASE}/marking-codes/materials`, {
        params: { version: APP_VERSION, search: query, limit: 35 }
      });
      if (res.data?.success && Array.isArray(res.data.data)) {
        setMaterialOptions(res.data.data);
      }
    } catch (e) {
      console.warn('Ошибка загрузки товаров:', e);
    } finally {
      setMaterialsLoading(false);
    }
  }, []);

  // Инициализация
  useEffect(() => {
    if (open) {
      if (initialFilters.dateFrom || initialFilters.dateTo) {
        setPeriodPreset('custom');
        setDateFrom(initialFilters.dateFrom || '');
        setDateTo(initialFilters.dateTo || '');
      } else {
        setPeriodPreset('all');
        setDateFrom('');
        setDateTo('');
      }

      setSelectedProduct(initialFilters.selectedProduct || null);

      if (initialFilters.selectedPharmacy) {
        setSelectedPharmacies([initialFilters.selectedPharmacy]);
      } else {
        setSelectedPharmacies([]);
      }

      if (initialFilters.selectedStorage) {
        setSelectedStorages([initialFilters.selectedStorage]);
      } else {
        setSelectedStorages([]);
      }

      if (initialFilters.type && initialFilters.type !== 'all') {
        setSelectedTypes([initialFilters.type]);
      } else {
        setSelectedTypes([]);
      }

      if (initialFilters.status && initialFilters.status !== 'all') {
        setSelectedStatuses([initialFilters.status]);
      } else {
        setSelectedStatuses([]);
      }

      setSelectedColumns(AVAILABLE_COLUMNS.map((c) => c.key));
      setShowColumnsConfig(false);
      setShowPreviewTable(false);
      setExportFormat('xlsx');

      fetchMaterials('');
    }
  }, [open, initialFilters, fetchMaterials]);

  const handlePeriodPresetChange = (preset: string) => {
    setPeriodPreset(preset);
    const today = new Date();
    const formatYMD = (d: Date) => d.toISOString().slice(0, 10);

    if (preset === 'today') {
      const dStr = formatYMD(today);
      setDateFrom(dStr);
      setDateTo(dStr);
    } else if (preset === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(today.getDate() - 7);
      setDateFrom(formatYMD(weekAgo));
      setDateTo(formatYMD(today));
    } else if (preset === 'month') {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(formatYMD(monthStart));
      setDateTo(formatYMD(today));
    } else if (preset === 'prevMonth') {
      const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      setDateFrom(formatYMD(prevMonthStart));
      setDateTo(formatYMD(prevMonthEnd));
    } else if (preset === 'all') {
      setDateFrom('');
      setDateTo('');
    }
  };

  const queryParams = useMemo(() => {
    const p: Record<string, string> = {
      version: APP_VERSION || '1.0.9'
    };

    if (dateFrom) p.dateFrom = dateFrom;
    if (dateTo) p.dateTo = dateTo;

    if (selectedProduct) p.product = selectedProduct;
    if (selectedPharmacies.length > 0) p.pharmacies = selectedPharmacies.join(',');
    if (selectedStorages.length > 0) p.storages = selectedStorages.join(',');
    if (selectedTypes.length > 0) p.types = selectedTypes.join(',');
    if (selectedStatuses.length > 0) p.statuses = selectedStatuses.join(',');
    if (selectedColumns.length > 0) p.columns = selectedColumns.join(',');

    p.format = exportFormat;
    return p;
  }, [
    dateFrom,
    dateTo,
    selectedProduct,
    selectedPharmacies,
    selectedStorages,
    selectedTypes,
    selectedStatuses,
    selectedColumns,
    exportFormat
  ]);

  // Запрос счетчика записей и первых 3 строк для предпросмотра
  const fetchPreviewData = useCallback(async () => {
    if (!open) return;
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    setIsCounting(true);
    setIsPreviewLoading(true);

    try {
      const [countRes, rowsRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/marking-codes/export-count`, {
          params: queryParams,
          signal: abortRef.current.signal
        }),
        axios.get(`${API_BASE}/admin/marking-codes`, {
          params: { ...queryParams, limit: 3, page: 1 },
          signal: abortRef.current.signal
        })
      ]);

      if (countRes.data?.success) {
        setPreviewCount(countRes.data.count);
      }
      if (rowsRes.data?.success && rowsRes.data.data?.items) {
        setPreviewRows(rowsRes.data.data.items);
      }
    } catch (e: any) {
      if (!axios.isCancel(e)) {
        console.error('Ошибка загрузки данных предпросмотра:', e);
      }
    } finally {
      setIsCounting(false);
      setIsPreviewLoading(false);
    }
  }, [open, queryParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPreviewData();
    }, 200);

    return () => clearTimeout(timer);
  }, [fetchPreviewData]);


  const handleReset = () => {
    setPeriodPreset('all');
    setDateFrom('');
    setDateTo('');
    setSelectedProduct(null);
    setSelectedPharmacies([]);
    setSelectedStorages([]);
    setSelectedTypes([]);
    setSelectedStatuses([]);
    setSelectedColumns(AVAILABLE_COLUMNS.map((c) => c.key));
    setShowColumnsConfig(false);
    setExportFormat('xlsx');
  };

  const handleDownload = () => {
    setIsDownloading(true);
    const searchParams = new URLSearchParams(queryParams);
    const exportUrl = `${API_BASE}/admin/marking-codes/export?${searchParams.toString()}`;
    window.open(exportUrl, '_blank');
    setTimeout(() => {
      setIsDownloading(false);
      onClose();
    }, 1200);
  };

  const validStatusesList = useMemo(
    () => statuses.filter((s) => s.zid !== 'all'),
    [statuses]
  );
  const validTypesList = useMemo(
    () => types.filter((t) => t.zid !== 'all'),
    [types]
  );

  // Соответствие колонок полям элемента для предпросмотра
  const renderCellContent = (colKey: string, item: any) => {
    switch (colKey) {
      case 'code':
        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.73rem',
              color: 'text.primary',
              px: 0.6,
              py: 0.2,
              borderRadius: 1,
              display: 'inline-block',
              letterSpacing: '-0.02em'
            }}
          >
            {item.code}
          </Typography>
        );
      case 'product':
        return (
          <Typography
            variant="body2"
            sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.primary' }}
            noWrap
            title={item.materialName}
          >
            {item.materialName || '—'}
          </Typography>
        );
      case 'type':
        return (
          <Typography variant="body2" sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
            {item.type || '—'}
          </Typography>
        );
      case 'status':
        return (
          <Typography variant="body2" sx={{ fontSize: '0.74rem', fontWeight: 500, color: 'text.primary' }}>
            {item.status || '—'}
          </Typography>
        );
      case 'documentNumber':
        return (
          <Typography variant="body2" sx={{ fontSize: '0.74rem', fontFamily: 'monospace', color: 'primary.main', fontWeight: 600 }}>
            #{item.documentNumber || '—'}
          </Typography>
        );
      case 'sender':
        return (
          <Typography variant="body2" sx={{ fontSize: '0.73rem', color: 'text.secondary' }}>
            {item.stockNameOut || '—'}
          </Typography>
        );
      case 'receiver':
        return (
          <Typography variant="body2" sx={{ fontSize: '0.73rem', color: 'text.primary' }}>
            {item.stockNameIn || '—'}
          </Typography>
        );
      case 'date':
        return (
          <Typography variant="body2" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
            {item.date || '—'}
          </Typography>
        );
      default:
        return '—';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 660,
          borderRadius: 3,
          boxShadow: '0 20px 48px rgba(15, 23, 42, 0.16)',
          bgcolor: 'background.paper',
          overflow: 'hidden'
        }
      }}
    >
      {/* ── Шапка: минимальные отступы, без иконки, формат справа ── */}
      <DialogTitle
        sx={{
          py: 1.5,
          px: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
          Выгрузка кодов маркировки
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Выбор формата в шапке */}
          <ToggleButtonGroup
            size="small"
            value={exportFormat}
            exclusive
            onChange={(_, val) => val && setExportFormat(val)}
            sx={{
              height: 30,
              '& .MuiToggleButton-root': {
                px: 1.25,
                py: 0,
                fontSize: '0.72rem',
                fontWeight: 600,
                textTransform: 'none',
                lineHeight: 1,
                whiteSpace: 'nowrap'
              }
            }}
          >
            <ToggleButton value="xlsx">
              Excel (.xlsx)
            </ToggleButton>
            <ToggleButton value="csv">
              CSV
            </ToggleButton>
          </ToggleButtonGroup>

          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ── Содержимое ── */}
      <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* 1. Период */}
        <Box>
          <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.75, fontSize: '0.8rem' }}>
            Период выгрузки
          </Typography>
          <Grid container spacing={1.25}>
            <Grid item xs={12} sm={periodPreset === 'custom' ? 4 : 12}>
              <FormControl size="small" fullWidth>
                <Select
                  value={periodPreset}
                  onChange={(e) => handlePeriodPresetChange(e.target.value)}
                  sx={{ borderRadius: 1.5, height: 40 }}
                >
                  <MenuItem value="all">За всё время</MenuItem>
                  <MenuItem value="today">Сегодня</MenuItem>
                  <MenuItem value="week">За последние 7 дней</MenuItem>
                  <MenuItem value="month">Текущий месяц</MenuItem>
                  <MenuItem value="prevMonth">Прошлый месяц</MenuItem>
                  <MenuItem value="custom">Свой период…</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {periodPreset === 'custom' && (
              <>
                <Grid item xs={6} sm={4}>
                  <TextField
                    size="small"
                    type="date"
                    label="Дата с"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 40 } }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    size="small"
                    type="date"
                    label="Дата по"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: dateFrom || undefined }}
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 40 } }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* 2. Товар / Препарат */}
        <Box>
          <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.75, fontSize: '0.8rem' }}>
            Товар / Препарат
          </Typography>
          <Autocomplete
            size="small"
            options={materialOptions}
            loading={materialsLoading}
            value={selectedProduct}
            onChange={(_, val) => setSelectedProduct(val)}
            onInputChange={(_, newInputValue) => {
              fetchMaterials(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Все товары (поиск по названию…)"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 40 } }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {materialsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            noOptionsText="Товар не найден"
            clearText="Очистить"
          />
        </Box>

        {/* 3. Аптеки */}
        <Box>
          <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.75, fontSize: '0.8rem' }}>
            Аптека
          </Typography>
          <Autocomplete
            multiple
            size="small"
            options={pharmacies}
            value={selectedPharmacies}
            onChange={(_, val) => setSelectedPharmacies(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedPharmacies.length === 0 ? 'Все аптеки сети (508)' : 'Добавить еще аптеку…'}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, minHeight: 40 } }}
              />
            )}
            renderTags={(val, getTagProps) =>
              val.map((option, index) => (
                <Chip
                  size="small"
                  label={option}
                  {...getTagProps({ index })}
                  sx={{ borderRadius: 1, height: 26, bgcolor: alpha(theme.palette.primary.main, 0.08) }}
                />
              ))
            }
            noOptionsText="Аптека не найдена"
          />
        </Box>

        {/* 4. Склады и Тип документа */}
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.75, fontSize: '0.8rem' }}>
              Склады
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                multiple
                value={selectedStorages}
                onChange={(e) =>
                  setSelectedStorages(
                    typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                  )
                }
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) return 'Все склады';
                  return selected.join(', ');
                }}
                sx={{ borderRadius: 1.5, height: 40 }}
              >
                {storages.map((st) => (
                  <MenuItem key={st} value={st}>
                    <Checkbox size="small" checked={selectedStorages.includes(st)} />
                    <ListItemText primary={st} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.75, fontSize: '0.8rem' }}>
              Тип накладной
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                multiple
                value={selectedTypes}
                onChange={(e) =>
                  setSelectedTypes(
                    typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                  )
                }
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) return 'Все типы';
                  return selected
                    .map((zid) => validTypesList.find((t) => String(t.zid) === String(zid))?.nameRu || zid)
                    .join(', ');
                }}
                sx={{ borderRadius: 1.5, height: 40 }}
              >
                {validTypesList.map((t) => (
                  <MenuItem key={String(t.zid)} value={t.zid}>
                    <Checkbox
                      size="small"
                      checked={selectedTypes.some((it) => String(it) === String(t.zid))}
                    />
                    <ListItemText primary={t.nameRu} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* 5. Статусы документов */}
        <Box>
          <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.75, fontSize: '0.8rem' }}>
            Статусы накладных
          </Typography>
          <FormControl size="small" fullWidth>
            <Select
              multiple
              value={selectedStatuses}
              onChange={(e) =>
                setSelectedStatuses(
                  typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                )
              }
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0) return 'Все статусы';
                if (selected.length === 1) {
                  return (
                    validStatusesList.find((s) => String(s.zid) === String(selected[0]))?.nameRu ||
                    selected[0]
                  );
                }
                return `Выбрано статусов: ${selected.length}`;
              }}
              sx={{ borderRadius: 1.5, height: 40 }}
            >
              {validStatusesList.map((st) => (
                <MenuItem key={String(st.zid)} value={st.zid}>
                  <Checkbox
                    size="small"
                    checked={selectedStatuses.some((it) => String(it) === String(st.zid))}
                  />
                  <ListItemText primary={st.nameRu} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* 6. Настройка колонок и предпросмотр */}
        <Box sx={{ pt: 0.25 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 0.5,
              userSelect: 'none'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <TableIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
              <Typography variant="body2" fontWeight={500} color="text.primary" sx={{ fontSize: '0.82rem' }}>
                Колонки в файле:
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedColumns.length === AVAILABLE_COLUMNS.length
                  ? 'Все (8 колонок)'
                  : `Выбрано ${selectedColumns.length} из ${AVAILABLE_COLUMNS.length}`}
              </Typography>
            </Box>

            {/* Кнопки предпросмотра и настройки колонок */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Button
                size="small"
                variant="text"
                color={showPreviewTable ? 'primary' : 'inherit'}
                startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                onClick={() => setShowPreviewTable((v) => !v)}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  py: 0.3,
                  px: 1,
                  minWidth: 0,
                  height: 26,
                  borderRadius: 1.25,
                  fontWeight: showPreviewTable ? 700 : 500,
                  bgcolor: showPreviewTable ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  color: showPreviewTable ? 'primary.main' : 'text.secondary',
                  '&:hover': {
                    bgcolor: showPreviewTable ? alpha(theme.palette.primary.main, 0.14) : 'action.hover'
                  }
                }}
              >
                {showPreviewTable ? 'Скрыть предпросмотр' : 'Предпросмотр'}
              </Button>

              <Button
                size="small"
                variant="text"
                onClick={() => setShowColumnsConfig((v) => !v)}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  py: 0.3,
                  px: 1,
                  minWidth: 0,
                  height: 26,
                  borderRadius: 1.25,
                  color: showColumnsConfig ? 'primary.main' : 'text.secondary',
                  bgcolor: showColumnsConfig ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  fontWeight: showColumnsConfig ? 700 : 500,
                  '&:hover': {
                    bgcolor: showColumnsConfig ? alpha(theme.palette.primary.main, 0.14) : 'action.hover'
                  }
                }}
              >
                {showColumnsConfig ? 'Свернуть' : 'Настроить…'}
              </Button>
            </Box>
          </Box>

          {/* Раскрывающийся блок выбора колонок */}
          <Collapse in={showColumnsConfig} timeout={180} unmountOnExit>
            <Box
              sx={{
                mt: 1,
                p: 1.25,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.background.default, 0.4),
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75
              }}
            >
              {AVAILABLE_COLUMNS.map((col) => {
                const isChecked = selectedColumns.includes(col.key);
                return (
                  <Chip
                    key={col.key}
                    label={col.label}
                    size="small"
                    clickable
                    onClick={() =>
                      setSelectedColumns((prev) =>
                        prev.includes(col.key)
                          ? prev.filter((c) => c !== col.key)
                          : [...prev, col.key]
                      )
                    }
                    icon={isChecked ? <CheckIcon sx={{ fontSize: '14px !important', color: 'primary.main' }} /> : undefined}
                    variant={isChecked ? 'filled' : 'outlined'}
                    sx={{
                      borderRadius: 1.5,
                      height: 28,
                      fontSize: '0.75rem',
                      fontWeight: isChecked ? 600 : 400,
                      bgcolor: isChecked ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
                      color: isChecked ? 'primary.main' : 'text.secondary',
                      borderColor: isChecked ? alpha(theme.palette.primary.main, 0.3) : 'divider',
                      '&:hover': {
                        bgcolor: isChecked ? alpha(theme.palette.primary.main, 0.16) : 'action.hover'
                      }
                    }}
                  />
                );
              })}
            </Box>
          </Collapse>

          {/* Раскрывающийся блок предпросмотра данных */}
          <Collapse in={showPreviewTable} timeout={200} unmountOnExit>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                mt: 1,
                borderRadius: 2,
                overflow: 'hidden',
                borderColor: 'divider',
                bgcolor: 'background.paper'
              }}
            >
              <TableContainer
                sx={{
                  maxHeight: 160,
                  overflowX: 'auto',
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': {
                    height: 5,
                    width: 5
                  },
                  '&::-webkit-scrollbar-track': {
                    bgcolor: 'transparent'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: alpha(theme.palette.text.disabled, 0.35),
                    borderRadius: 3,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.text.disabled, 0.5)
                    }
                  }
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {AVAILABLE_COLUMNS.filter((c) => selectedColumns.includes(c.key)).map((col) => (
                        <TableCell
                          key={col.key}
                          sx={{
                            py: 0.9,
                            px: 1.25,
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: 'text.secondary',
                            bgcolor: alpha(theme.palette.action.hover, 0.12),
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isPreviewLoading ? (
                      [0, 1, 2].map((idx) => (
                        <TableRow key={idx}>
                          {AVAILABLE_COLUMNS.filter((c) => selectedColumns.includes(c.key)).map((col) => (
                            <TableCell key={col.key} sx={{ py: 0.8, px: 1.25 }}>
                              <Skeleton variant="text" width={col.key === 'code' ? 120 : 70} height={18} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : previewRows.length > 0 ? (
                      previewRows.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            '&:last-child td': { border: 0 },
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) }
                          }}
                        >
                          {AVAILABLE_COLUMNS.filter((c) => selectedColumns.includes(c.key)).map((col) => (
                            <TableCell
                              key={col.key}
                              sx={{
                                py: 0.75,
                                px: 1.25,
                                fontSize: '0.74rem',
                                whiteSpace: 'nowrap',
                                maxWidth: col.key === 'product' ? 190 : undefined,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {renderCellContent(col.key, row)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={selectedColumns.length || 1}
                          align="center"
                          sx={{ py: 2.5, color: 'text.secondary', fontSize: '0.75rem' }}
                        >
                          {previewCount === 0 ? 'Нет данных по выбранным фильтрам' : 'Загрузка предпросмотра…'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Collapse>
        </Box>
      </DialogContent>

      <Divider />

      {/* ── Подвал: счетчик + кнопки в одну строку ── */}
      <DialogActions
        sx={{
          py: 1.5,
          px: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.paper'
        }}
      >
        {/* Счетчик записей */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            height: 32
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Записей:
          </Typography>
          {isCounting ? (
            <CircularProgress size={12} thickness={5} />
          ) : (
            <Typography variant="caption" fontWeight={700} color="primary.main">
              {previewCount !== null ? previewCount.toLocaleString('ru-RU') : '—'}
            </Typography>
          )}
        </Box>

        {/* Кнопки действий */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Button
            size="small"
            onClick={handleReset}
            color="inherit"
            sx={{ textTransform: 'none', fontSize: '0.8rem', color: 'text.secondary', minWidth: 0, px: 1 }}
          >
            Сбросить
          </Button>
          <Button
            size="small"
            onClick={onClose}
            color="inherit"
            sx={{ textTransform: 'none', fontSize: '0.8rem', minWidth: 0, px: 1.25 }}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={isDownloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon fontSize="small" />}
            onClick={handleDownload}
            disabled={isDownloading || previewCount === 0 || selectedColumns.length === 0}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              height: 34,
              px: 2,
              borderRadius: 1.5,
              whiteSpace: 'nowrap',
              boxShadow: 'none'
            }}
          >
            {isDownloading ? 'Формирование…' : `Скачать ${exportFormat.toUpperCase()}`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
